// mds-dojo — dijit/_KeyNavMixin — FAITHFUL port of references/dijit/_KeyNavMixin.js.
// Mixin allowing arrow-key + letter-key navigation of child/descendant widgets
// (used by Menu/MenuBar/Toolbar/Tree). declare from the faithful runtime.
//
// mds ADAPTATIONS (behavior-preserving):
//  - dojo `on.selector(childSelector, "focusin")` delegation is done via the mds
//    onSelector() shim (mds dojo/on has no delegation). See ./_onSelector.
//  - unused `dojo/_base/array` import dropped.
import declare from '../dojo/_base/declare-runtime'; // declare
import domAttr from '../dojo/dom-attr'; // domAttr.set
import keys from '../dojo/keys'; // keys.END keys.HOME, keys.LEFT_ARROW etc.
import lang from '../dojo/_base/lang'; // lang.hitch
import on from '../dojo/on';
import registry from './registry';
import _FocusMixin from './_FocusMixin'; // to make _onBlur() work
import { onSelector } from './_onSelector';

export default declare('dijit._KeyNavMixin', _FocusMixin, {
  // summary:
  //		A mixin to allow arrow key and letter key navigation of child or descendant widgets.

  // tabIndex: String
  //		Tab index of the container; same as HTML tabIndex attribute.
  tabIndex: '0',

  // childSelector: [protected abstract] Function||String
  //		Selector (passed to on.selector()) used to identify what to treat as a child widget.
  childSelector: null,

  postCreate: function () {
    this.inherited(arguments);

    // Set tabIndex on this.domNode.  Will be automatic after #7381 is fixed.
    domAttr.set(this.domNode, 'tabIndex', this.tabIndex);

    if (!this._keyNavCodes) {
      var keyCodes: any = this._keyNavCodes = {};
      keyCodes[keys.HOME] = lang.hitch(this, 'focusFirstChild');
      keyCodes[keys.END] = lang.hitch(this, 'focusLastChild');
      keyCodes[this.isLeftToRight() ? keys.LEFT_ARROW : keys.RIGHT_ARROW] = lang.hitch(this, '_onLeftArrow');
      keyCodes[this.isLeftToRight() ? keys.RIGHT_ARROW : keys.LEFT_ARROW] = lang.hitch(this, '_onRightArrow');
      keyCodes[keys.UP_ARROW] = lang.hitch(this, '_onUpArrow');
      keyCodes[keys.DOWN_ARROW] = lang.hitch(this, '_onDownArrow');
    }

    var self = this,
      childSelector = typeof this.childSelector == 'string'
        ? this.childSelector
        : lang.hitch(this, 'childSelector');
    this.own(
      on(this.domNode, 'keypress', lang.hitch(this, '_onContainerKeypress')),
      on(this.domNode, 'keydown', lang.hitch(this, '_onContainerKeydown')),
      on(this.domNode, 'focus', lang.hitch(this, '_onContainerFocus')),
      onSelector(this.containerNode, childSelector, 'focusin', function (this: any, evt: any) {
        self._onChildFocus(registry.getEnclosingWidget(this), evt);
      }),
    );
  },

  _onLeftArrow: function () {
    // Called on left arrow key, or right arrow key if widget is in RTL mode.
    // Should go back to the previous child in horizontal container widgets like Toolbar.
  },

  _onRightArrow: function () {
    // Called on right arrow key, or left arrow key if widget is in RTL mode.
    // Should go to the next child in horizontal container widgets like Toolbar.
  },

  _onUpArrow: function () {
    // Called on up arrow key. Should go to the previous child in vertical container widgets like Menu.
  },

  _onDownArrow: function () {
    // Called on down arrow key. Should go to the next child in vertical container widgets like Menu.
  },

  focus: function () {
    // Default focus() implementation: focus the first child.
    this.focusFirstChild();
  },

  _getFirstFocusableChild: function () {
    // Returns first child that can be focused.
    // Leverage _getNextFocusableChild() to skip disabled children
    return this._getNextFocusableChild(null, 1);	// dijit/_WidgetBase
  },

  _getLastFocusableChild: function () {
    // Returns last child that can be focused.
    // Leverage _getNextFocusableChild() to skip disabled children
    return this._getNextFocusableChild(null, -1);	// dijit/_WidgetBase
  },

  focusFirstChild: function () {
    // Focus the first focusable child in the container.
    this.focusChild(this._getFirstFocusableChild());
  },

  focusLastChild: function () {
    // Focus the last focusable child in the container.
    this.focusChild(this._getLastFocusableChild());
  },

  focusChild: function (/*dijit/_WidgetBase*/ widget: any, /*Boolean*/ last?: any) {
    // Focus specified child widget.
    // widget: Reference to container's child widget
    // last: If true and if widget has multiple focusable nodes, focus the last one

    if (!widget) {
      return;
    }

    if (this.focusedChild && widget !== this.focusedChild) {
      this._onChildBlur(this.focusedChild);	// used to be used by _MenuBase
    }
    widget.set('tabIndex', this.tabIndex);	// for IE focus outline to appear, must set tabIndex before focus
    widget.focus(last ? 'end' : 'start');

    // Don't set focusedChild here, because the focus event should trigger a call to _onChildFocus(), which will
    // set it.
  },

  _onContainerFocus: function (evt: any) {
    // Handler for when the container itself gets focus.
    // Initially the container itself has a tabIndex, but when it gets focus, switch
    // focus to first child.

    // Ignore spurious focus events:
    //	1. focus on a child widget bubbles on FF
    //	2. on IE, clicking the scrollbar of a select dropdown moves focus from the focused child item to me
    if (evt.target !== this.domNode || this.focusedChild) {
      return;
    }

    this.focus();
  },

  _onFocus: function () {
    // When the container gets focus by being tabbed into, or a descendant gets focus by being clicked,
    // set the container's tabIndex to -1 (don't remove as that breaks Safari 4) so that tab or shift-tab
    // will go to the fields after/before the container, rather than the container itself
    domAttr.set(this.domNode, 'tabIndex', '-1');

    this.inherited(arguments);
  },

  _onBlur: function (evt: any) {
    // When focus is moved away the container, and its descendant (popup) widgets,
    // then restore the container's tabIndex so that user can tab to it again.
    domAttr.set(this.domNode, 'tabIndex', this.tabIndex);
    if (this.focusedChild) {
      this.focusedChild.set('tabIndex', '-1');
      this.lastFocusedChild = this.focusedChild;
      this._set('focusedChild', null);
    }
    this.inherited(arguments);
  },

  _onChildFocus: function (/*dijit/_WidgetBase*/ child: any) {
    // Called when a child widget gets focus, either by user clicking it, or
    // programatically by arrow key handling code.

    if (child && child != this.focusedChild) {
      if (this.focusedChild && !this.focusedChild._destroyed) {
        // mark that the previously focusable node is no longer focusable
        this.focusedChild.set('tabIndex', '-1');
      }

      // mark that the new node is the currently selected one
      child.set('tabIndex', this.tabIndex);
      this.lastFocused = child;		// back-compat for Tree, remove for 2.0
      this._set('focusedChild', child);
    }
  },

  _searchString: '',
  // multiCharSearchDuration: Number
  //		If multiple characters are typed where each keystroke happens within
  //		multiCharSearchDuration of the previous keystroke, search for nodes matching all the keystrokes.
  multiCharSearchDuration: 1000,

  onKeyboardSearch: function (/*dijit/_WidgetBase*/ item: any, /*Event*/ evt: any, /*String*/ searchString?: any, /*Number*/ numMatches?: any) {
    // When a key is pressed that matches a child item, this method is called so that a
    // widget can take appropriate action is necessary.
    if (item) {
      this.focusChild(item);
    }
  },

  _keyboardSearchCompare: function (/*dijit/_WidgetBase*/ item: any, /*String*/ searchString: any) {
    // Compares the searchString to the widget's text label, returning:
    //	* -1: a high priority match  and stop searching
    //	* 0: not a match
    //	* 1: a match but keep looking for a higher priority match

    var element = item.domNode,
      text = item.label || (element.focusNode ? element.focusNode.label : '') || element.innerText || element.textContent || '',
      currentString = text.replace(/^\s+/, '').substr(0, searchString.length).toLowerCase();

    return (!!searchString.length && currentString == searchString) ? -1 : 0; // stop searching after first match by default
  },

  _onContainerKeydown: function (evt: any) {
    // When a key is pressed, if it's an arrow key etc. then it's handled here.

    var func = this._keyNavCodes[evt.keyCode];
    if (func) {
      func(evt, this.focusedChild);
      evt.stopPropagation();
      evt.preventDefault();
      this._searchString = ''; // so a DOWN_ARROW b doesn't search for ab
    } else if (evt.keyCode == keys.SPACE && this._searchTimer && !(evt.ctrlKey || evt.altKey || evt.metaKey)) {
      evt.stopImmediatePropagation(); // stop a11yclick and _HasDropdown from seeing SPACE if we're doing keyboard searching
      evt.preventDefault(); // stop IE from scrolling, and most browsers (except FF) from sending keypress
      this._keyboardSearch(evt, ' ');
    }
  },

  _onContainerKeypress: function (evt: any) {
    // When a printable key is pressed, it's handled here, searching by letter.
    if (evt.charCode <= keys.SPACE || evt.ctrlKey || evt.altKey || evt.metaKey) {
      return;
    }

    evt.preventDefault();
    evt.stopPropagation();

    this._keyboardSearch(evt, String.fromCharCode(evt.charCode).toLowerCase());
  },

  _keyboardSearch: function (/*Event*/ evt: any, /*String*/ keyChar: any) {
    // Perform a search of the widget's options based on the user's keyboard activity.
    var
      matchedItem: any = null,
      searchString: any,
      numMatches = 0,
      search = lang.hitch(this, function (this: any) {
        if (this._searchTimer) {
          this._searchTimer.remove();
        }
        this._searchString += keyChar;
        var allSameLetter = /^(.)\1*$/.test(this._searchString);
        var searchLen = allSameLetter ? 1 : this._searchString.length;
        searchString = this._searchString.substr(0, searchLen);
        this._searchTimer = this.defer(function (this: any) { // this is the "success" timeout
          this._searchTimer = null;
          this._searchString = '';
        }, this.multiCharSearchDuration);
        var currentItem = this.focusedChild || null;
        if (searchLen == 1 || !currentItem) {
          currentItem = this._getNextFocusableChild(currentItem, 1); // skip current
          if (!currentItem) {
            return;
          } // no items
        }
        var stop = currentItem;
        do {
          var rc = this._keyboardSearchCompare(currentItem, searchString);
          if (!!rc && numMatches++ == 0) {
            matchedItem = currentItem;
          }
          if (rc == -1) { // priority match
            numMatches = -1;
            break;
          }
          currentItem = this._getNextFocusableChild(currentItem, 1);
        } while (currentItem && currentItem != stop);
      });

    search();
    this.onKeyboardSearch(matchedItem, evt, searchString, numMatches);
  },

  _onChildBlur: function (/*dijit/_WidgetBase*/ /*===== widget =====*/) {
    // Called when focus leaves a child widget to go to a sibling widget.
    // Used to be used by MenuBase.js (remove for 2.0)
  },

  _getNextFocusableChild: function (child: any, dir: any) {
    // Returns the next or previous focusable descendant, compared to "child".
    // dir: 1 = after, -1 = before

    var wrappedValue = child;
    do {
      if (!child) {
        child = this[dir > 0 ? '_getFirst' : '_getLast']();
        if (!child) { break; }
      } else {
        child = this._getNext(child, dir);
      }
      if (child != null && child != wrappedValue && child.isFocusable()) {
        return child;	// dijit/_WidgetBase
      }
    } while (child != wrappedValue);
    // no focusable child found
    return null;	// dijit/_WidgetBase
  },

  _getFirst: function () {
    // Returns the first child.
    return null;	// dijit/_WidgetBase
  },

  _getLast: function () {
    // Returns the last descendant.
    return null;	// dijit/_WidgetBase
  },

  _getNext: function (child: any, dir: any) {
    // Returns the next descendant, compared to "child".
    if (child) {
      child = child.domNode;
      while (child) {
        child = child[dir < 0 ? 'previousSibling' : 'nextSibling'];
        if (child && 'getAttribute' in child) {
          var w = registry.byNode(child);
          if (w) {
            return w; // dijit/_WidgetBase
          }
        }
      }
    }
    return null;	// dijit/_WidgetBase
  },
});
